<script setup lang="ts">
const meta = inject('meta', ref())

const status: string = 'PUBLISHED'

const route = useRoute()

const page = ref(parseInt(route.query.p || 1))
const strapiData = ref(null)

const { public: config } = useRuntimeConfig()
const locale = config.strapiLocale
const { $t } = useI18n()

async function fetchData() {
  const filters = {
    Type: {
      eq: 'BlogDetail',
    },
  }

  if (route.query.tag) {
    filters.Tags = {
      Tag: {
        eq: route.query.tag,
      },
    }
  }

  const { data: response } = await useAsyncGraphqlQuery('blogOverview', {
    documentId: meta.value.strapi_id,
    locale,
    status,
    page: page.value,
    pageSize: 6,
    filters,
  }, useGraphqlDefaultClient())

  strapiData.value = response?.value?.data
}

await fetchData()

watch(() => route.query.p, async (p) => {
  page.value = parseInt(p || 1)
  await fetchData()
})

watch(() => route.query.tag, async () => {
  await fetchData()
})

const allBlogPages = computed(() => strapiData.value?.allBlogPages)

const pageTitle = computed(
  () => strapiData.value?.blogOverview?.PageTitle || '',
)

const metaTitle = computed(
  () => strapiData.value?.blogOverview?.MetaData?.MetaTitle || pageTitle.value + ' | ' + $t('meta.title'),
)

const metaDescription = computed(
  () => strapiData.value?.blogOverview?.MetaData?.MetaDescription || $t('meta.description'),
)

const { setBreadcrumbs } = useBreadcrumbs()
setBreadcrumbs({
  label: pageTitle.value,
  parents: meta.value.parents,
})

const { pageView } = useDataLayer()
pageView('blog-overview', metaTitle.value)

const updatePage = (newPage: number) => {
  const query = { ...route.query }
  if (newPage === 1) {
    delete query.p
  }
  else {
    query.p = newPage.toString()
  }
  navigateTo({ query })
}

useHead({
  title: metaTitle,
  meta: [
    { name: 'description', content: metaDescription },
    { name: 'robots', content: strapiData.value?.blogOverview?.MetaData?.MetaRobots?.replace('_', ', ') },
  ],
})

const { canonical } = useSeo()
canonical(computed(() => route.path))
</script>

<template>
  <div class="blog-overview">
    <AppBreadcrumbs v-if="$route.path !== '/'" />

    <StrapiSlice
      class="blog-overview__content"
      :slice="{
        Settings: {
          Title: strapiData?.blogOverview?.PageTitle || '',
          SliceWidth: 'large-column',
          Description: strapiData?.blogOverview?.Description || '',
          BackgroundColor: '#fff',
        },
      }"
      variant="vertical"
      :index="0"
      force-heading-tag="h1"
    >
      <div
        v-if="strapiData?.tags?.length"
        class="blog-overview__blog-tags"
      >
        <BlogTag
          :tag="{ Tag: $t('All') }"
          :reset="true"
        />

        <BlogTag
          v-for="tag in strapiData?.tags"
          :key="tag"
          :tag="tag"
        />
      </div>

      <template v-if="allBlogPages?.nodes?.length">
        <div class="blog-overview__blog-grid">
          <CmsTile
            v-for="blog in allBlogPages.nodes"
            :key="blog"
            :page="blog"
            :lazy="false"
          />
        </div>

        <PaginatorPages
          v-if="allBlogPages?.pageInfo?.pageCount > 1"
          class="blog-overview__blog-pages"
          :current-page="allBlogPages?.pageInfo?.page"
          :total-pages="allBlogPages?.pageInfo?.pageCount"
          @update-page="updatePage"
        />
      </template>
    </StrapiSlice>

    <UiHeadingSection>
      <StrapiSlices
        v-if="strapiData?.blogOverview?.Slices?.length"
        :slices="strapiData.blogOverview.Slices"
        :slice-offset="1"
      />
    </UiHeadingSection>

    <!-- <pre>parentDocumentId: {{ parentDocumentId }}</pre> -->
    <!-- <pre>subOverviews: {{ subOverviews }}</pre> -->
    <!-- <pre>subBlogPages: {{ subBlogPages }}</pre> -->
    <!-- <pre>allBlogPages: {{ allBlogPages }}</pre> -->
    <!-- <pre>strapiData: {{ strapiData }}</pre> -->
    <!-- <pre>meta: {{ meta }}</pre> -->
  </div>
</template>

<style>
@import url("~layers/pages/~blog-overview.css");
</style>
